import { graphql } from 'react-relay';
import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type { NotificationAction } from '../graphql/__types__/CreateContentMentionNotificationAction.ts';

import type {
	useMentionNotificationMutationCreateContentMentionNotificationActionMutation as useMentionNotificationMutationCreateContentMentionNotificationActionMutation$data,
	useMentionNotificationMutationCreateContentMentionNotificationActionMutationVariables as useMentionNotificationMutationCreateContentMentionNotificationActionMutation$variables,
} from './__types__/useMentionNotificationMutationCreateContentMentionNotificationActionMutation.ts';
const useMentionNotificationMutationCreateContentMentionNotificationActionMutation = graphql`
	mutation useMentionNotificationMutationCreateContentMentionNotificationActionMutation(
		$contentId: ID!
		$mentions: [ContentMention]!
	) {
		createContentMentionNotificationAction(input: { contentId: $contentId, mentions: $mentions }) {
			contentId
			success
			errors {
				message
			}
		}
	}
`;
type MentionNotificationMutationProps = {
	contentId: string;
	setError: React.Dispatch<React.SetStateAction<Error | null>>;
	showDontNotifyFailedFlag: () => void;
};

export type MentionNotificationType = {
	mentionLocalId: string;
	mentionedUserAccountId: string;
	notificationAction: NotificationAction;
};

type SendCreateMentionNotificationProps = {
	mentions: MentionNotificationType[];
};

type UseMentionNotificationMutationResult = {
	sendMentionNotification: (props: SendCreateMentionNotificationProps) => void;
	sendCancelMentionNotification: (props: SendCreateMentionNotificationProps) => void;
	contentMentionNotificationError?: ApolloErrorInterop;
};

export const useMentionNotificationMutation = ({
	contentId,
	setError,
	showDontNotifyFailedFlag,
}: MentionNotificationMutationProps): UseMentionNotificationMutationResult => {
	const [createContentMentionNotificationAction, { error: contentMentionNotificationError }] =
		useMutationInterop<
			useMentionNotificationMutationCreateContentMentionNotificationActionMutation$data,
			useMentionNotificationMutationCreateContentMentionNotificationActionMutation$variables
		>(useMentionNotificationMutationCreateContentMentionNotificationActionMutation);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendMentionNotification = useCallback(
		({ mentions }: SendCreateMentionNotificationProps) => {
			const fireAnalyticsEvent = (action: 'succeeded' | 'failed') => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'mentionsNotifications',
						action,
						actionSubject: 'mentionInputMutation',
						attributes: {
							mentions: mentions.map((mention) => ({
								mentionedUserId: mention.mentionedUserAccountId,
								mentionId: mention.mentionLocalId,
							})),
							contentId,
						},
					},
				}).fire();
			};

			// NCS draft syncs need to happen prior to mutation in order for
			// email content to show up. A 15 second buffer after user closes
			// the flag notification will give draft syncs a chance to happen.
			setTimeout(() => {
				// tells backend to send notificiation for the list of mention IDs
				createContentMentionNotificationAction({
					variables: {
						contentId,
						mentions,
					},
				})
					.then(() => {
						fireAnalyticsEvent('succeeded');
					})
					.catch(() => {
						//TODO: create new error flag that handles multiple names
						fireAnalyticsEvent('failed');
					});
			}, 15000);
		},
		[contentId, createAnalyticsEvent, createContentMentionNotificationAction],
	);

	const sendCancelMentionNotification = useCallback(
		({ mentions }: SendCreateMentionNotificationProps) => {
			createContentMentionNotificationAction({
				variables: {
					contentId,
					mentions,
				},
			}).catch((error: Error) => {
				setError(error);
				showDontNotifyFailedFlag();
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentId, createContentMentionNotificationAction],
	);

	return {
		sendMentionNotification,
		sendCancelMentionNotification,
		contentMentionNotificationError,
	};
};
